'use client';

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Download, FolderPlus, Palette, RefreshCw, Edit2, Undo, Redo, HelpCircle, Lock, Film, Users, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MemoPreviewPlayer } from './MemoPreviewPlayer';
import { MemoTranscriptEditor } from './MemoTranscriptEditor';
import { CaptionStyleModal } from '../momento/CaptionStyleModal';
import { ExportQualityModal } from '../momento/ExportQualityModal';
import type { Memo } from '../momento/MomentoContext';
import type { CaptionStyleName } from '@/lib/utils/caption-styles';
import { ExportQuality, getDefaultQualityForPlan } from '@/lib/utils/export-quality';

interface MemoViewProps {
  memo: Memo;
  playbackId: string;
  vttUrl?: string;
  words?: Array<{
    text: string;
    start: number;
    end: number;
    confidence: number;
    speaker?: string;
  }>;
  utterances?: any[];
  speakerMappings?: Record<string, string>;
  onExit: () => void;
  onExport?: (memoId: string, captionStyle?: CaptionStyleName, quality?: ExportQuality) => void;
  onAddToCollection?: (memo: Memo) => void;
  creditBalance?: {
    balance: number;
    planType: 'free' | 'plus' | 'pro' | 'enterprise';
    isLowBalance: boolean;
  } | null;
}

export function MemoView({
  memo,
  playbackId,
  vttUrl,
  words = [],
  utterances = [],
  speakerMappings = {},
  onExit,
  onExport,
  onAddToCollection,
  creditBalance,
}: MemoViewProps) {
  const { start, end } = memo.source_timerange;
  const [currentTime, setCurrentTime] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const playerRef = useRef<HTMLDivElement>(null);

  // Check if user can export (Plus+ plan required)
  const isFreePlan = creditBalance?.planType === 'free';

  const cutCount = memo.edit_plan?.cuts?.length || 0;
  const captionEditCount = memo.edit_plan?.captions?.length || 0;
  const isExporting = memo.status === 'exporting';
  const isExported = memo.status === 'exported' && memo.export_url;
  const [selectedCaptionStyle, setSelectedCaptionStyle] = useState<CaptionStyleName>('minimal');
  const [selectedQuality, setSelectedQuality] = useState<ExportQuality>(
    getDefaultQualityForPlan(creditBalance?.planType || 'free')
  );
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [showSpeakerEditor, setShowSpeakerEditor] = useState(false);

  // Detect unlabeled speakers
  const unlabeledSpeakers = utterances && speakerMappings
    ? utterances.reduce((speakers, u) => {
        if (u.speaker && !speakerMappings[u.speaker]) {
          speakers.add(u.speaker);
        }
        return speakers;
      }, new Set<string>())
    : new Set<string>();

  const hasUnlabeledSpeakers = unlabeledSpeakers.size > 0;
  const needsSpeakerGuidance = memo.metadata?.guidance === 'label_speakers' || hasUnlabeledSpeakers;

  // Debug: Log when memo prop changes
  useEffect(() => {
    console.log('[MemoView] Memo prop updated:', {
      memoId: memo.id,
      cuts: memo.edit_plan?.cuts?.length || 0,
      captions: memo.edit_plan?.captions?.length || 0,
      captionDetails: memo.edit_plan?.captions,
      updatedAt: memo.updated_at,
    });
  }, [memo]);

  // Manual refresh handler - re-fetches memo without leaving modal
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('[MemoView] Fetching fresh memo data...');
      const response = await fetch(`/api/memo/${memo.id}`);
      if (!response.ok) {
        throw new Error('Failed to refresh memo');
      }
      const data = await response.json();

      console.log('[MemoView] Fresh memo received:', {
        cuts: data.memo.edit_plan?.cuts?.length,
        captions: data.memo.edit_plan?.captions?.length,
        captionDetails: data.memo.edit_plan?.captions,
      });

      // Need parent component to update activeMemo
      // For now, this just logs - parent needs to expose refresh callback
      alert('Memo data fetched - check console logs. Parent component needs to call setActiveMemo to update UI.');
    } catch (error) {
      console.error('[MemoView] Refresh error:', error);
      alert('Failed to refresh memo');
    } finally {
      setIsRefreshing(false);
    }
  };
  const [showCaptionStyleModal, setShowCaptionStyleModal] = useState(false);

  const handleTimeUpdate = (time: number) => {
    setCurrentTime(time);
  };

  const handleSeekToWord = (timeSeconds: number) => {
    // Use the seekTo method exposed by MemoPreviewPlayer
    if (playerRef.current && (playerRef.current as any).seekTo) {
      (playerRef.current as any).seekTo(timeSeconds);
    }
  };

  // Undo/redo handlers
  const handleUndo = async () => {
    try {
      const response = await fetch(`/api/memo/${memo.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'undo' })
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.error || 'Cannot undo');
        return;
      }

      toast.success('Undid last edit');
      handleRefresh();
    } catch (error) {
      console.error('[MemoView] Undo error:', error);
      toast.error('Failed to undo');
    }
  };

  const handleRedo = async () => {
    try {
      const response = await fetch(`/api/memo/${memo.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'redo' })
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.error || 'Cannot redo');
        return;
      }

      toast.success('Redid edit');
      handleRefresh();
    } catch (error) {
      console.error('[MemoView] Redo error:', error);
      toast.error('Failed to redo');
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [memo.id]);

  return (
    <div className="flex-1 flex flex-col bg-muted/10 h-full max-h-screen overflow-hidden">
      {/* Memo Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold">📝 Editing:</span>
          <span className="text-xs text-muted-foreground truncate max-w-[200px]">{memo.title}</span>
          {(cutCount > 0 || captionEditCount > 0) && (
            <>
              <span className="text-xs text-muted-foreground">•</span>
              <div className="flex items-center gap-1">
                {cutCount > 0 && (
                  <Badge variant="destructive" className="text-[10px] h-4">
                    {cutCount} cut{cutCount > 1 ? 's' : ''}
                  </Badge>
                )}
                {captionEditCount > 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 bg-yellow-100 text-yellow-700">
                    {captionEditCount} edit{captionEditCount > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onExit}
          className="h-7 px-2 text-xs"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Exit Memo Mode
        </Button>
      </div>

      {/* Video Player - Optimized height for editing */}
      <div
        ref={playerRef}
        className="relative w-full bg-black flex-shrink-0 h-[35vh] max-h-[360px]"
      >
        <MemoPreviewPlayer
          playbackId={playbackId}
          startTime={start}
          endTime={end}
          editPlan={memo.edit_plan}
          vttUrl={vttUrl}
          autoPlay={false}
          onTimeUpdate={handleTimeUpdate}
          className="w-full h-full"
        />
      </div>

      {/* Speaker Labeling Guidance Banner */}
      {needsSpeakerGuidance && (
        <Alert className="mx-4 mt-3 mb-0 border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-900 dark:text-blue-100">Speaker Labeling Needed</AlertTitle>
          <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
            {unlabeledSpeakers.size > 0 && (
              <p className="mb-2">
                I found <strong>{unlabeledSpeakers.size} unlabeled speaker{unlabeledSpeakers.size > 1 ? 's' : ''}</strong> in this video:{' '}
                {Array.from(unlabeledSpeakers).map(s => `Speaker ${s}`).join(', ')}.
                Label them to create posts in their authentic voice.
              </p>
            )}
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="default"
                onClick={() => {
                  // TODO: Open SpeakerEditor in Sheet/Dialog
                  toast.info('Speaker editor coming soon! For now, use the Edit Video tab on the post.');
                }}
                className="h-8 bg-blue-600 hover:bg-blue-700"
              >
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Label Speakers
              </Button>
              {memo.metadata?.guidance === 'label_speakers' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onExit();
                    toast.success('Return to chat and say "speakers labeled" to continue post generation');
                  }}
                  className="h-8"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Continue to Chat
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="px-4 py-3 border-b flex gap-2 bg-background flex-shrink-0">
        <Button
          variant={isEditingMode ? "default" : "outline"}
          size="sm"
          onClick={() => setIsEditingMode(!isEditingMode)}
          title={isEditingMode ? "Exit edit mode" : "Enable direct transcript editing"}
        >
          <Edit2 className="h-4 w-4 mr-1" />
          {isEditingMode ? 'Done Editing' : 'Edit Transcript'}
        </Button>
        {isEditingMode && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUndo}
              title="Undo last edit (Cmd+Z)"
            >
              <Undo className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRedo}
              title="Redo (Cmd+Shift+Z)"
            >
              <Redo className="h-4 w-4" />
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm p-4">
                  <div className="space-y-2 text-xs">
                    <p className="font-semibold text-sm">Editing Shortcuts</p>
                    <div className="space-y-1">
                      <div><strong>Double-click</strong> word → Edit text inline</div>
                      <div><strong>Right-click</strong> word → Cut/Keep options</div>
                      <div><strong>Shift-click</strong> → Select word range</div>
                      <div><strong>Enter</strong> → Save inline edit</div>
                      <div><strong>Tab</strong> → Save & jump to next word</div>
                      <div><strong>Escape</strong> → Cancel inline edit</div>
                      <div><strong>Cmd+Z</strong> → Undo last edit</div>
                      <div><strong>Cmd+Shift+Z</strong> → Redo edit</div>
                    </div>
                    <p className="text-muted-foreground pt-2">
                      Or use chat for bulk operations like "remove all filler words"
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCaptionStyleModal(true)}
          title="Choose caption style for export"
        >
          <Palette className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowQualityModal(true)}
          title="Choose export quality"
        >
          <Film className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Refresh memo to see latest edits"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
        {onAddToCollection && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onAddToCollection(memo)}
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            Add to Collection
          </Button>
        )}
        {onExport && (
          isExported ? (
            <div className="flex-1 flex gap-2">
              <Button
                variant="default"
                size="sm"
                className="flex-1"
                onClick={() => window.open(memo.export_url!, '_blank')}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onExport(memo.id, selectedCaptionStyle, selectedQuality)}
                title="Export again with different settings"
              >
                Retry
              </Button>
            </div>
          ) : isFreePlan ? (
            // Free Plan: Locked export with billing redirect
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
              onClick={() => {
                window.location.href = '/dashboard/settings/billing';
              }}
            >
              <Lock className="h-4 w-4 mr-2" />
              Export - Upgrade Required
            </Button>
          ) : (
            // Paid Plan: Normal export with quality badge
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              onClick={() => onExport(memo.id, selectedCaptionStyle, selectedQuality)}
              disabled={isExporting}
            >
              <Download className="h-4 w-4 mr-2" />
              <span className="flex items-center gap-2">
                {isExporting ? 'Exporting...' : 'Export'}
                {!isExporting && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0 h-4 bg-white/20 text-white border-white/30"
                  >
                    {selectedQuality === '4k' ? '4K' : selectedQuality === '1080' ? '1080p' : selectedQuality === 'hd' ? '720p' : 'SD'}
                  </Badge>
                )}
              </span>
            </Button>
          )
        )}
      </div>

      {/* Edit Legend - Always Visible */}
      <div className="px-6 py-2 border-b bg-muted/10 flex-shrink-0">
        <div className="flex items-center gap-6 text-xs text-muted-foreground">
          <span className="text-[10px] uppercase tracking-wide font-medium text-foreground/60">
            Legend:
          </span>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-red-100 dark:bg-red-900/30 rounded border border-red-300 dark:border-red-700"></div>
            <span>Deleted</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-yellow-100 dark:bg-yellow-900/40 rounded border border-yellow-300 dark:border-yellow-700"></div>
            <span>Caption Edit</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-yellow-200 dark:bg-yellow-700 rounded border border-yellow-300 dark:border-yellow-600"></div>
            <span>Playing Now</span>
          </div>
        </div>
      </div>

      {/* Transcript Editor - Scrollable */}
      {words.length > 0 && (
        <div className="flex-1 overflow-y-auto scroll-smooth">
          <div className="p-4">
            <MemoTranscriptEditor
              key={`${memo.id}-${memo.edit_plan?.captions?.length || 0}-${memo.edit_plan?.cuts?.length || 0}-${memo.updated_at}`}
              words={words}
              utterances={utterances}
              speakerMappings={speakerMappings}
              editPlan={memo.edit_plan}
              currentTime={currentTime}
              onSeekToWord={handleSeekToWord}
              isEditingMode={isEditingMode}
              memoId={memo.id}
              className="h-full"
            />
          </div>
        </div>
      )}

      {/* Caption Style Modal */}
      <CaptionStyleModal
        open={showCaptionStyleModal}
        onOpenChange={setShowCaptionStyleModal}
        selectedStyle={selectedCaptionStyle}
        onSelectStyle={setSelectedCaptionStyle}
      />

      {/* Export Quality Modal */}
      <ExportQualityModal
        open={showQualityModal}
        onOpenChange={setShowQualityModal}
        selectedQuality={selectedQuality}
        onSelectQuality={setSelectedQuality}
        currentPlan={creditBalance?.planType || 'free'}
      />
    </div>
  );
}
